import { WorksheetAssociatedTestSection } from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { Guid } from 'Lib/Guid';
import { combine } from 'Lib/Hooks/Selectors';
import { pipe } from 'ramda';
import React from 'react';
import { useSelector } from 'react-redux';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../Layout';

const Section: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedReferenceDensityId = useSelector(createWorksheetSelector!('ReferenceDensity.ImportedTest.Id')) as Guid;
  const stabiliserUsed = useSelector(createWorksheetSelector!('ReferenceDensityResults.StabiliserUsed')) as boolean;
  const particleDensityIsAssumed = useSelector(
    pipe(createWorksheetSelector!('IsParticleDensityAssumed'), valueOrDefault(false))
  ) as boolean;
  const lockIsParticleDensityAssumed = useSelector(
    pipe(createWorksheetSelector!('ReferenceDensityResults.ParticleDensity'), hasValue)
  );
  const referenceDensityIsReducedTest = useSelector(
    pipe(createWorksheetSelector!('ReferenceDensityResults.IsReducedTest'), valueOrDefault(false))
  );
  const confirmIsParticleDensityAssumedChange = useSelector(
    combine(
      [createWorksheetSelector!('IsParticleDensityAssumed'), createWorksheetSelector!('ParticleDensityAssumed')],
      (isParticleDensityAssumed, assumedParticleDensity) =>
        hasValue(isParticleDensityAssumed) && isParticleDensityAssumed && hasValue(assumedParticleDensity)
    )
  );

  return (
    <PageSection heading="Laboratory Density Results">
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="IsParticleDensityAssumed"
          disabled={lockIsParticleDensityAssumed}
          confirm={confirmIsParticleDensityAssumedChange}
          confirmText={
            'Changing Particle Density Determination will delete the entered Assumed Particle Density. Are you sure you want to continue?'
          }
        />
        <Field input={<NumericInput />} name="ParticleDensityAssumed" visible={particleDensityIsAssumed} />
      </ListFieldSection>
      <WorksheetAssociatedTestSection
        label="Laboratory Density Results"
        name="ReferenceDensity"
        testType="Maximum Dry Density"
        associatedTestModel={{
          imported: {
            testSearchLabel: 'Reference/Sample ID',
            importPropertyName: 'ReferenceDensity',
            importedTestResults: (
              <ListFieldSection>
                <Field
                  input={<TextOutput />}
                  label="Reference ID"
                  name="ReferenceDensityResults.ReferenceId"
                  visible={referenceDensityIsReducedTest}
                />
                <Field input={<TextOutput />} label="Supplier" name="ReferenceDensityResults.Supplier" />
                <Field input={<TextOutput />} label="Source" name="ReferenceDensityResults.Source" />
                <Field input={<TextOutput />} label="Material" name="ReferenceDensityResults.Material" />
                <Field input={<TextOutput />} label="Sample Location" name="ReferenceDensityResults.SampleLocation" />
                <Field
                  input={<TextOutput />}
                  label="Stabilised Material?"
                  name="ReferenceDensityResults.StabiliserUsed"
                  format={{ type: FormatTypes.YesNo }}
                />
                <Field
                  input={<TextOutput />}
                  label="Stabiliser Type"
                  name="ReferenceDensityResults.AdditiveMaterialTypeName"
                  visible={stabiliserUsed}
                />
                <Field
                  input={<TextOutput />}
                  label="Stabiliser Amount (%)"
                  name="ReferenceDensityResults.AdditiveMaterialAmount"
                  visible={stabiliserUsed}
                  format={{ nearest: 0.1 }}
                />
                <Field
                  input={<TextOutput />}
                  label="Maximum Density Method"
                  name="ReferenceDensityResults.MaximumDensityMethod"
                />
                <Field
                  input={<TextOutput />}
                  label="Maximum Dry Density"
                  name="ReferenceDensityResults.MaximumDryDensity"
                />
                <Field
                  input={<TextOutput />}
                  label="Optimum Moisture Content (%)"
                  name="ReferenceDensityResults.OptimumMoistureContent"
                />
                <Field
                  input={<TextOutput />}
                  label="Particle Density Method"
                  name="ReferenceDensityResults.ParticleDensityMethod"
                />
                <Field input={<TextOutput />} label="Particle Density" name="ReferenceDensityResults.ParticleDensity" />
              </ListFieldSection>
            ),
            importModel: { Id: importedReferenceDensityId },
            queryParams: {
              limit: 50,
            },
          },
        }}
      />
    </PageSection>
  );
};

const BsReferenceDensitySection = withInjectedProps(Section);

export { BsReferenceDensitySection };
export default BsReferenceDensitySection;
